import { ChevronDown } from 'lucide-react';
import type { ComponentProps } from 'react';
import { cn } from './cn';
import { controlClass, useField } from './field-control';
import { Icon } from './icons';

/** Classes that place the control in its row (width, margins, flex item), not the ones that dress the select itself. */
const LAYOUT = /^(?:[\w-]+:)*-?(?:w-|min-w-|max-w-|m[trblxyse]?-|flex-|grow|shrink|basis-|self-|order-|col-|row-)/;

function splitLayout(className: string | undefined): [layout: string, own: string] {
  const layout: string[] = [];
  const own: string[] = [];
  for (const token of (className ?? '').split(/\s+/).filter(Boolean)) (LAYOUT.test(token) ? layout : own).push(token);
  return [layout.join(' '), own.join(' ')];
}

/**
 * The native `<select>`, dressed: a pill with a Lucide chevron inside it (`appearance-none` drops the browser's own
 * arrow, which sat against the edge). It stays native on purpose: the iOS wheel and the Android picker are the most
 * native choice list there is, and the e2e specs drive it with `selectOption`. `color-scheme` is inherited from the
 * root (tokens.css), so the opened list follows the theme. 44 px high and 16 px text under a coarse pointer.
 *
 * `className` keeps working as on a bare select: width, margins and flex classes go to the wrapper that holds the
 * chevron (`w-auto`, `w-20`), the rest (`h-8 px-2`) to the select. Inside a `Field` it takes the id and ARIA links.
 * `invalid` (or an error on the `Field`) draws the error border and sets `aria-invalid`.
 */
export function Select({ className, invalid, children, ...rest }: ComponentProps<'select'> & { invalid?: boolean }) {
  const field = useField();
  const [layout, own] = splitLayout(className);
  return (
    <span className={cn('relative inline-flex w-full align-middle', layout)}>
      <select
        {...field}
        aria-invalid={invalid || field['aria-invalid'] || undefined}
        className={cn(controlClass, 'h-10 min-w-0 cursor-pointer appearance-none truncate rounded-full pl-4 pr-8 pointer-coarse:h-11', own, 'pr-8')}
        {...rest}
      >
        {children}
      </select>
      <Icon icon={ChevronDown} size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-fg-muted" />
    </span>
  );
}
